/**
 * The /looker filter-bar "● data as of" pill, which now also carries each ad
 * channel's sync (the retired "Data freshness" app box, 2026-10-13).
 *
 * Every ad channel fresh → today's green pill, as a button to the per-channel
 * detail. Any channel not fresh (stale, never synced, or failing) → warning
 * colour and a count. Loading or the all-workspaces view → the plain date pill.
 * A failed read keeps the date and says so beside it: never shown as fresh.
 *
 * @module components/looker/DataStatusPill
 */
import React, { useCallback, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { platformLabel } from '@/utils/platformLabel';
import { formatLastSync } from '@/components/settings/formatLastSync';
import { isNotFresh, type FreshChannel } from '@/services/looker/lookerAppData';
import type { ChannelFreshnessState } from '@/hooks/useLookerChannelFreshness';
import { POPOVER_PANEL_CLASS, useDismiss, usePanelPlacement } from './statusPopover';

const DASH = '—';
const PILL = 'rounded-full border bg-white px-2.5 py-1 text-xs font-medium tabular-nums';
const FRESH_TONE = 'border-success-600 text-success-700';
const STALE_TONE = 'border-warning-700 text-warning-700';

/** Only a channel that is not fresh gets a colour; an error keeps its own. */
const STATE_CLASS: Record<FreshChannel['state'], string> = {
  ok: 'text-neutral-800',
  stale: 'font-semibold text-warning-700',
  never: 'font-semibold text-warning-700',
  error: 'font-semibold text-error-700',
};

const StateWord: React.FC<{ channel: FreshChannel }> = ({ channel }) => {
  const { t } = useTranslation();
  // Literal keys, so the locale integrity test sees them.
  const text: Record<FreshChannel['state'], string> = {
    ok: t('looker.dataStatus.state.ok'),
    stale: t('looker.dataStatus.state.stale'),
    error: t('looker.dataStatus.state.error'),
    never: t('looker.dataStatus.state.never'),
  };
  return (
    <span data-fresh-state={channel.state} title={channel.error ?? undefined} className={`shrink-0 text-xs ${STATE_CLASS[channel.state]}`}>
      {text[channel.state]}
    </span>
  );
};

const ChannelRow: React.FC<{ c: FreshChannel }> = ({ c }) => {
  const { t } = useTranslation();
  return (
    <li className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 py-2">
      <div className="min-w-0">
        <p className="font-semibold text-neutral-900">{platformLabel(c.platform)}</p>
        <p className="text-xs text-neutral-700">
          {c.lastSyncedAt ? t('looker.dataStatus.syncedAgo', { ago: formatLastSync(c.lastSyncedAt, t) }) : DASH}
          {' · '}
          <span className="tabular-nums">{t('looker.dataStatus.dataThrough', { date: c.dataThrough ?? DASH })}</span>
        </p>
      </div>
      <StateWord channel={c} />
    </li>
  );
};

const Panel: React.FC<{ id: string; channels: FreshChannel[]; style: React.CSSProperties | undefined }> = ({ id, channels, style }) => {
  const { t } = useTranslation();
  return (
    <div id={id} role="region" aria-label={t('looker.dataStatus.heading')} className={POPOVER_PANEL_CLASS} style={style}>
      <p className="text-xs font-semibold text-neutral-800">{t('looker.dataStatus.heading')}</p>
      {channels.length === 0 ? (
        <p className="py-2 text-xs text-neutral-800">{t('looker.dataStatus.none')}</p>
      ) : (
        <>
          <ul className="divide-y divide-neutral-100">
            {channels.map((c) => <ChannelRow key={c.platform} c={c} />)}
          </ul>
          <p className="mt-2 inline-block rounded-md bg-neutral-100 px-2 py-1 text-xs font-medium text-neutral-800">
            {t('looker.dataStatus.unsettled')}
          </p>
        </>
      )}
    </div>
  );
};

/** "● data as of …", "⚠ data as of … · 2 stale", or the status alone when there is no date. */
const useLabel = (date: string | null, notFresh: number): string => {
  const { t } = useTranslation();
  const parts = [
    ...(date ? [t('looker.dataAsOf', { date })] : []),
    ...(notFresh > 0 ? [t('looker.dataStatus.stale', { n: notFresh })] : []),
  ];
  if (parts.length === 0) return `● ${t('looker.dataStatus.allFresh')}`;
  return `${notFresh > 0 ? '⚠' : '●'} ${parts.join(' · ')}`;
};

const StatusButton: React.FC<{ date: string | null; channels: FreshChannel[] }> = ({ date, channels }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  const panelId = useId();
  const close = useCallback(() => setOpen(false), []);
  useDismiss(open, root, close);
  const placement = usePanelPlacement(open, root);
  const notFresh = channels.filter(isNotFresh).length;
  const label = useLabel(date, notFresh);

  return (
    <div ref={root} className="relative min-w-0 max-w-full">
      <button
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        title={t('looker.dataStatus.details')}
        onClick={() => setOpen((v) => !v)}
        className={`${PILL} min-w-0 max-w-full whitespace-normal break-words text-left hover:bg-neutral-50 ${notFresh > 0 ? STALE_TONE : FRESH_TONE}`}
      >
        {label}
      </button>
      {open && <Panel id={panelId} channels={channels} style={placement ?? undefined} />}
    </div>
  );
};

const DatePill: React.FC<{ date: string }> = ({ date }) => {
  const { t } = useTranslation();
  return <span className={`${PILL} ${FRESH_TONE}`}>{`● ${t('looker.dataAsOf', { date })}`}</span>;
};

export const DataStatusPill: React.FC<{ latestDataDate: string | null; state: ChannelFreshnessState }> = ({ latestDataDate, state }) => {
  const { t } = useTranslation();
  if (state.status === 'ready' && (latestDataDate || state.channels.length > 0)) {
    return <StatusButton date={latestDataDate} channels={state.channels} />;
  }
  if (state.status === 'failed') {
    return (
      <>
        {latestDataDate && <DatePill date={latestDataDate} />}
        <span className="text-xs text-neutral-700">{t('looker.dataStatus.couldntLoad')}</span>
      </>
    );
  }
  // Idle (all workspaces), loading, or nothing at all to say.
  return latestDataDate ? <DatePill date={latestDataDate} /> : null;
};
